import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateActivityRecommendations, optimizeItinerarySequence } from '../../../services/recommendationService';

const RecommendationPanel = ({
  tripInfo,
  itinerary,
  availableActivities,
  onAddActivity,
  onOptimizeItinerary
}) => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('suggestions');
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [userInterests, setUserInterests] = useState(['Beach & Islands', 'Food & Cuisine', 'Cultural Heritage']);
  const [budgetPreference, setBudgetPreference] = useState('medium');

  const userPreferences = {
    interests: userInterests,
    budgetRange: budgetPreference,
    travelStyle: 'balanced',
    activityLevel: 'moderate'
  };

  const handleGenerateRecommendations = async () => {
    setLoading(true);
    setError('');

    try {
      const totalBudget = calculateTotalBudget() + 50000; // Add buffer for recommendations
      const result = await generateActivityRecommendations({
        destination: tripInfo?.destination || 'Philippines',
        travelDates: {
          startDate: tripInfo?.startDate,
          endDate: tripInfo?.endDate
        },
        userPreferences,
        currentItinerary: itinerary,
        availableActivities,
        budget: totalBudget,
        travelers: tripInfo?.travelers || 2
      });

      setRecommendations(result);
      setActiveTab('suggestions');
    } catch (err) {
      setError(err?.message || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeSequence = async () => {
    setOptimizing(true);
    setError('');

    try {
      const result = await optimizeItinerarySequence({
        currentItinerary: itinerary,
        destination: tripInfo?.destination || 'Philippines',
        travelDates: {
          startDate: tripInfo?.startDate,
          endDate: tripInfo?.endDate
        },
        preferences: userPreferences
      });

      setOptimizationResults(result);
      setActiveTab('optimization');
    } catch (err) {
      setError(err?.message || 'Failed to optimize itinerary');
    } finally {
      setOptimizing(false);
    }
  };

  const calculateTotalBudget = () => {
    let total = 0;
    itinerary?.forEach(day => {
      day?.timeSlots?.forEach(slot => {
        if (slot?.activity) {
          total += slot?.activity?.cost || 0;
        }
      });
    });
    return total;
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-blue-600 bg-blue-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      activity: 'Waves',
      accommodation: 'Hotel',
      food: 'Utensils',
      transport: 'Car',
      wellness: 'Heart',
      sightseeing: 'Camera',
      shopping: 'ShoppingBag',
      entertainment: 'Music'
    };
    return icons?.[category] || 'MapPin';
  };

  const availableInterests = [
    'Beach & Islands',
    'Food & Cuisine',
    'Cultural Heritage',
    'Adventure Sports',
    'Nature & Wildlife',
    'Shopping',
    'Nightlife',
    'Wellness & Spa',
    'Historical Sites',
    'Local Markets'
  ];

  const toggleInterest = (interest) => {
    setUserInterests(prev => 
      prev?.includes(interest)
        ? prev?.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">AI-Powered Recommendations</h2>
            <p className="text-sm text-muted-foreground">Smart suggestions based on your budget, interests & trip duration</p>
          </div>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={handleGenerateRecommendations}
          disabled={loading}
          iconName="RefreshCw"
          iconPosition="left"
        >
          {loading ? 'Analyzing...' : 'Get Smart Suggestions'}
        </Button>
      </div>

      {/* Preferences Section */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Settings" size={16} />
          Customize Your Preferences
        </h3>
        
        {/* Interests */}
        <div className="mb-4">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Your Interests (Select multiple)
          </label>
          <div className="flex flex-wrap gap-2">
            {availableInterests?.map(interest => (
              <button
                key={interest}
                onClick={() => toggleInterest(interest)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                  userInterests?.includes(interest)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                }`}
              >
                {interest}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Preference */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Budget Preference
          </label>
          <div className="flex gap-2">
            {['budget', 'medium', 'luxury']?.map(level => (
              <button
                key={level}
                onClick={() => setBudgetPreference(level)}
                className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                  budgetPreference === level
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                }`}
              >
                {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-error mb-1">Error</p>
            <p className="text-sm text-error/80">{error}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('suggestions')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'suggestions' ?'text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Smart Suggestions
          {activeTab === 'suggestions' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('optimization')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'optimization' ?'text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Route Optimization
          {activeTab === 'optimization' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'insights' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Trip Insights
          {activeTab === 'insights' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">Analyzing your preferences and budget...</p>
          <p className="text-xs text-muted-foreground mt-1">Generating personalized recommendations</p>
        </div>
      )}

      {!loading && !recommendations && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Sparkles" size={32} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Get Personalized Recommendations</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Our AI will analyze your budget, interests, and trip duration to suggest the perfect activities for your journey.
          </p>
          <Button
            variant="default"
            onClick={handleGenerateRecommendations}
            iconName="Sparkles"
            iconPosition="left"
          >
            Generate Smart Suggestions
          </Button>
        </div>
      )}

      {!loading && recommendations && activeTab === 'suggestions' && (
        <div className="space-y-4">
          {/* Budget Overview */}
          {recommendations?.budgetAnalysis && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="DollarSign" size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Budget Analysis</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Remaining Budget</p>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                        ₱{recommendations?.budgetAnalysis?.remainingBudget?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Budget Utilization</p>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                        {recommendations?.budgetAnalysis?.budgetUtilization?.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  {recommendations?.budgetAnalysis?.budgetWarnings?.length > 0 && (
                    <div className="space-y-1">
                      {recommendations?.budgetAnalysis?.budgetWarnings?.map((warning, idx) => (
                        <p key={idx} className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
                          <Icon name="AlertCircle" size={12} className="mt-0.5 flex-shrink-0" />
                          {warning}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recommendations List */}
          <div className="space-y-3">
            {recommendations?.recommendations?.map((rec, index) => (
              <div
                key={index}
                className="p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-all"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={getCategoryIcon(rec?.category)} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{rec?.title}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          getMatchScoreColor(rec?.matchScore)
                        }`}>
                          {rec?.matchScore}% Match
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{rec?.reasoning}</p>
                      
                      {/* Interest Alignment */}
                      {rec?.interestAlignment && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-primary mb-1">Why this matches you:</p>
                          <p className="text-xs text-muted-foreground">{rec?.interestAlignment}</p>
                        </div>
                      )}

                      {/* Budget Impact */}
                      {rec?.budgetImpact && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Budget Impact:</p>
                          <p className="text-xs text-muted-foreground">{rec?.budgetImpact}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          Day {rec?.suggestedDay} • {rec?.suggestedTime}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          {rec?.estimatedDuration}h
                        </span>
                        <span className="flex items-center gap-1 font-semibold text-primary">
                          <Icon name="DollarSign" size={12} />
                          ₱{rec?.estimatedCost?.toLocaleString()}
                        </span>
                      </div>

                      {rec?.preferenceMatch?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {rec?.preferenceMatch?.map((pref, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                              {pref}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddActivity(rec)}
                    iconName="Plus"
                  >
                    Add
                  </Button>
                </div>

                {/* Additional Details */}
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Best Time</p>
                    <p className="text-xs font-medium text-foreground">{rec?.bestTimeToVisit}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Weather</p>
                    <p className="text-xs font-medium text-foreground">{rec?.weatherConsideration}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Crowd Level</p>
                    <p className="text-xs font-medium text-foreground">{rec?.crowdLevel}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Saving Opportunities */}
          {recommendations?.budgetAnalysis?.savingOpportunities?.length > 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2 flex items-center gap-2">
                <Icon name="TrendingDown" size={16} />
                Money-Saving Tips
              </h3>
              <ul className="space-y-1">
                {recommendations?.budgetAnalysis?.savingOpportunities?.map((tip, idx) => (
                  <li key={idx} className="text-xs text-green-700 dark:text-green-300 flex items-start gap-2">
                    <Icon name="Check" size={12} className="mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!loading && recommendations && activeTab === 'insights' && (
        <div className="space-y-4">
          {/* Trip Insights */}
          {recommendations?.tripInsights && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={16} className="text-purple-600 dark:text-purple-400" />
                  <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">Pacing Recommendation</h3>
                </div>
                <p className="text-xs text-purple-700 dark:text-purple-300">
                  {recommendations?.tripInsights?.pacingRecommendation}
                </p>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Heart" size={16} className="text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Interest Coverage</h3>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  {recommendations?.tripInsights?.interestCoverage}
                </p>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} className="text-green-600 dark:text-green-400" />
                  <h3 className="text-sm font-semibold text-green-900 dark:text-green-100">Duration Optimization</h3>
                </div>
                <p className="text-xs text-green-700 dark:text-green-300">
                  {recommendations?.tripInsights?.durationOptimization}
                </p>
              </div>

              <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-orange-600 dark:text-orange-400" />
                  <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100">Balance Score</h3>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-orange-200 dark:bg-orange-900 rounded-full h-2">
                    <div 
                      className="bg-orange-600 dark:bg-orange-400 h-2 rounded-full transition-all"
                      style={{ width: `${recommendations?.tripInsights?.balanceScore}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-orange-900 dark:text-orange-100">
                    {recommendations?.tripInsights?.balanceScore}/100
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Optimization Suggestions */}
          {recommendations?.optimizationSuggestions?.length > 0 && (
            <div className="p-4 bg-card rounded-lg border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Lightbulb" size={16} />
                Optimization Suggestions
              </h3>
              <div className="space-y-3">
                {recommendations?.optimizationSuggestions?.map((suggestion, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Zap" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-foreground mb-1">{suggestion?.type}</p>
                      <p className="text-xs text-muted-foreground mb-1">{suggestion?.suggestion}</p>
                      <p className="text-xs text-primary font-medium">Impact: {suggestion?.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecommendationPanel;